import { useEffect, useMemo, useRef, useState } from "react"
import { useCompanyControllerGetIntegrationSetupStatusQuery } from "~/ui-rtk/api/companyApi"
import useCurrentCompany from "./current-company"

const INITIAL_POLLING_INTERVAL = 2000 // 2 seconds - first few checks
const MID_POLLING_INTERVAL = 5000 // 5 seconds - after initial checks
const FINAL_POLLING_INTERVAL = 10000 // 10 seconds - for remaining time
const INITIAL_ATTEMPTS = 5 // First 5 attempts at 2s interval (10 seconds total)
const MID_ATTEMPTS = 15 // Next 10 attempts at 5s interval (50 seconds total)
const JITTER_PERCENTAGE = 0.2 // 20% jitter to prevent thundering herd
const MAX_REASONABLE_AGE = 300000 // 5 minutes - if company is older, assume setup should be done

/**
 * Calculate polling interval with exponential backoff and jitter
 * Prevents thundering herd by adding random jitter to intervals
 */
const getPollingInterval = (attemptCount: number): number => {
  let baseInterval: number

  if (attemptCount < INITIAL_ATTEMPTS) {
    // First 5 attempts: every 2s (first 10 seconds)
    baseInterval = INITIAL_POLLING_INTERVAL
  } else if (attemptCount < INITIAL_ATTEMPTS + MID_ATTEMPTS) {
    // Next 10 attempts: every 5s (next 50 seconds)
    baseInterval = MID_POLLING_INTERVAL
  } else {
    // Remaining time: every 10s
    baseInterval = FINAL_POLLING_INTERVAL
  }

  // Add jitter: random variation of ±20% to prevent synchronized requests
  // This helps distribute load when multiple users sign up simultaneously
  const jitter = baseInterval * JITTER_PERCENTAGE * (Math.random() * 2 - 1) // -20% to +20%
  return Math.max(1000, Math.round(baseInterval + jitter)) // Minimum 1 second
}

export default function useIntegrationSetupStatus() {
  const currentCompany = useCurrentCompany()
  const companyIdRef = useRef<string | undefined>(currentCompany?.id)
  const [integrationsReady, setIntegrationsReady] = useState<
    boolean | undefined
  >(undefined)
  const [attemptCount, setAttemptCount] = useState<number>(0)
  const lastPollTimestampRef = useRef<number>(0)
  const jitteredIntervalRef = useRef<number | null>(null)
  const lastAttemptCountRef = useRef<number>(-1)

  // Reset state when company changes (triggers state reset)
  useEffect(() => {
    if (companyIdRef.current !== currentCompany?.id) {
      companyIdRef.current = currentCompany?.id
      setIntegrationsReady(undefined)
      setAttemptCount(0)
      lastPollTimestampRef.current = 0
      lastAttemptCountRef.current = -1
      jitteredIntervalRef.current = null
    }
  }, [currentCompany?.id])

  // Calculate company creation time
  const companyCreatedAt = useMemo(() => {
    if (!currentCompany?.createdAt) {
      return null // Use null to indicate missing creation date
    }
    return new Date(currentCompany.createdAt).getTime()
  }, [currentCompany?.createdAt])

  // Calculate elapsed time since company creation
  // Calculate on every render since Date.now() changes over time
  // If creation date is unknown, treat as old company to prevent indefinite polling
  const elapsedSinceCreation =
    companyCreatedAt === null
      ? MAX_REASONABLE_AGE + 1
      : Date.now() - companyCreatedAt

  // Calculate polling interval with exponential backoff and jitter
  // RTK Query supports reactive pollingInterval
  // Jitter is calculated once per attempt count to avoid constant rescheduling
  const pollingInterval = useMemo(() => {
    if (!currentCompany?.id) {
      return undefined
    }

    // Stop polling if company is too old
    if (elapsedSinceCreation >= MAX_REASONABLE_AGE) {
      return undefined
    }

    // Stop polling if integrations are ready
    if (integrationsReady === true) {
      return undefined
    }

    // Recalculate jittered interval only when attempt count changes
    // This prevents constant rescheduling while still providing jitter per attempt
    if (lastAttemptCountRef.current !== attemptCount) {
      jitteredIntervalRef.current = getPollingInterval(attemptCount)
      lastAttemptCountRef.current = attemptCount
    }

    return jitteredIntervalRef.current ?? undefined
  }, [
    currentCompany?.id,
    elapsedSinceCreation,
    integrationsReady,
    attemptCount,
  ])

  // Single query call with reactive polling interval
  // RTK Query will automatically adjust polling when pollingInterval changes between renders
  const { data: setupStatus, isLoading } =
    useCompanyControllerGetIntegrationSetupStatusQuery(
      { id: currentCompany?.id || "" },
      {
        skip: !currentCompany?.id,
        pollingInterval,
      },
    )

  // Update state with current integrationsReady status and track poll attempts
  // Stop polling immediately if integrations are ready
  useEffect(() => {
    if (setupStatus?.integrationsReady !== undefined) {
      const now = Date.now()
      const minTimeBetweenPolls = 1000 // Minimum 1 second between poll increments

      // Stop polling immediately if integrations are ready
      if (setupStatus.integrationsReady) {
        setIntegrationsReady(true)
        setAttemptCount(0) // Reset attempt count
        lastPollTimestampRef.current = 0
        lastAttemptCountRef.current = -1
        jitteredIntervalRef.current = null
      } else {
        setIntegrationsReady(false)
        // Increment attempt count only if enough time has passed
        // This prevents double-counting from rapid cache updates or re-renders
        if (now - lastPollTimestampRef.current >= minTimeBetweenPolls) {
          setAttemptCount(prev => prev + 1)
          lastPollTimestampRef.current = now
        }
      }
    }
  }, [setupStatus?.integrationsReady])

  return {
    integrationsReady: setupStatus?.integrationsReady ?? false,
    isLoading: isLoading || !setupStatus,
    message: setupStatus?.message,
  }
}
